import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
    BehaviorSubject,
    distinctUntilChanged,
    map,
    Observable,
    Subject,
} from 'rxjs';
import { SavedDashboardViewHelper } from 'src/app/components/dashboard/saved-dashboard-view/saved-dashboard-view-helper';
import { SavedContextMetadata } from 'src/app/components/dashboard/saved-dashboard-view/saved-dashboard-view.component';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth.service';
import { ConfigService } from '../config.service';
import { DataService } from '../data.service';
import { FuncService } from '../functions-api.service';
import { LoggerService } from '../logger/logger.service';
import { NotificationService } from '../notification.service';
import { IDashboardContext } from '../query-context/models/dashboard-context.model';
import { DashboardView } from '../query-context/models/dashboard-view.model';
import { IQueryContext } from '../query-context/models/IQueryContext';
import { QueryContextService } from '../query-context/query-context.service';

/**
 * Options for the DashboardService.
 */
export interface IDashboardInitOptions {
    /** Function to compare saved dashboard views. Used for sorting. */
    savedDashboardViewsCompareFn?: (
        a: DashboardView,
        b: DashboardView
    ) => number;
    /** Select initial dashboard view callback. If defined then called on dashboard startup */
    selectInitialDashboardViewClb?: (
        dashboardViews: DashboardView[]
    ) => DashboardView;
    /** Name of desired fullscreen container
     * @default `main-container`
     */
    fullscreenClassName?: string;
}

/**
 * DashboardService used for dashboard related tasks
 */
@Injectable()
export class DashboardService {
    /** User friendly name of the dashboard. */
    DASHBOARD_NAME: string = '';
    /** Unique ID of the dashboard. */
    DASHBOARD_ID: string = '';
    /** The default context for the dashboard. */
    INITIAL_QUERY_CONTEXT: IQueryContext | undefined;

    /** Options for the DashboardService. */
    dashboardInitOptions: IDashboardInitOptions = {};

    /** Subject to emit when initial context is restored. */
    public initialQueryContextRestored$ = new Subject<void>();

    /** Contains the current DashboardView. If no view is selected, it is null. */
    private _currentDashboardView: DashboardView | null = null;

    /** BehaviorSubject to emit the current dashboard view to subscribers. */
    public currentDashboardView$ = new BehaviorSubject<DashboardView | null>(
        null
    );
    /** Contains the saved dashboard views. It is empty by default. It is loaded from Firestore. */
    private savedDashboardViews: DashboardView[] = [];
    /** BehaviorSubject to emit the saved dashboard views to subscribers. */
    public savedDashboardViews$ = new BehaviorSubject<DashboardView[]>([]);

    /** Contains the reason why the current dashboard view cannot
     * be saved. If the view can be saved, it is an empty string. */
    public cannotSaveCurrentDashboardViewReason$ = new BehaviorSubject<string>(
        ''
    );
    /** Emits the context ID of the newly saved dashboard view. */
    public newDashboardViewSaved$ = new Subject<string>();

    /** Used to store the current dashboard context. */
    private _currentDashboardContext: IDashboardContext = {};
    public currentDashboardContext$ =
        new BehaviorSubject<IDashboardContext | null>({});

    public _activeFullscreenItemId$ = new BehaviorSubject<string | null>(null);
    public readonly activeFullscreenItemId$ =
        this._activeFullscreenItemId$.pipe(distinctUntilChanged());

    get fullscreenContainerClassName(): HTMLElement | null {
        const className =
            this.dashboardInitOptions.fullscreenClassName ?? 'main-container';
        return document.querySelector(`.${className}`) as HTMLElement | null;
    }

    constructor(
        private authService: AuthService,
        private queryContextService: QueryContextService,
        private data: DataService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private notification: NotificationService,
        private logger: LoggerService,
        private func: FuncService,
        private config: ConfigService,
        private modal: NzModalService
    ) {}

    /**
     * Sets the element as active in fullscreen mode.
     *
     * Updates the internal subject only if the ID changed.
     * @param itemId - Dashboard item identifier.
     */
    public setActiveFullscreen(itemId: string) {
        if (this._activeFullscreenItemId$.value !== itemId) {
            this._activeFullscreenItemId$.next(itemId);
        }
    }

    /**
     * Clears the active fullscreen item.
     *
     * If `itemId` is provided, clears only when it matches the active one.
     * Also triggers applying any pending query context.
     * @param itemId - Dashboard item identifier.
     */
    public clearActiveFullscreen(itemId: string) {
        if (this._activeFullscreenItemId$.value === itemId) {
            this._activeFullscreenItemId$.next(null);
        }
        this.queryContextService.applyPendingQueryContext();
    }

    /**
     * Emits whether the given item is allowed to load data.
     *
     * Loading is allowed when no item is in fullscreen or when `itemId` matches the active fullscreen item.
     * @param itemId - Dashboard item identifier.
     * @returns Observable that emits `true` when loading is permitted.
     */
    public isDataLoadingAllowed$(itemId: string): Observable<boolean> {
        return this.activeFullscreenItemId$.pipe(
            map((fullscreenId) => !fullscreenId || fullscreenId === itemId),
            distinctUntilChanged()
        );
    }

    public initializeDashboard(
        dashboardName: string,
        dashboardId: string,
        defaultContext: IQueryContext,
        dashboardInitOptions?: IDashboardInitOptions
    ) {
        this.DASHBOARD_NAME = dashboardName;
        this.DASHBOARD_ID = dashboardId;
        this.INITIAL_QUERY_CONTEXT = defaultContext;
        this.dashboardInitOptions = dashboardInitOptions ?? {};
        this.setCannotSaveCurrentDashboardViewReason(this.currentDashboardView);
    }

    get currentDashboardView() {
        return this._currentDashboardView;
    }

    set currentDashboardView(newDashboardView: DashboardView | null) {
        this._currentDashboardView = newDashboardView;
        this.currentDashboardView$.next(newDashboardView);
        this.setCannotSaveCurrentDashboardViewReason(newDashboardView);
    }

    /**
     * Sets the reason why the current dashboard view cannot be saved.
     * @param newDashboardView
     * @emits cannotSaveCurrentDashboardViewReason$
     */
    private setCannotSaveCurrentDashboardViewReason(
        newDashboardView: DashboardView | null
    ) {
        // if null
        if (!newDashboardView) {
            this.cannotSaveCurrentDashboardViewReason$.next('No view selected');
            return;
        }
        if (
            newDashboardView.userEmail !==
            this.authService.auth.currentUser?.email
        ) {
            this.cannotSaveCurrentDashboardViewReason$.next(
                `Cannot save other user's views (${newDashboardView.userEmail})`
            );
            return;
        }
        // if no name
        if (!newDashboardView.name) {
            this.cannotSaveCurrentDashboardViewReason$.next(
                'Cannot save quick view'
            );
            return;
        }
        this.cannotSaveCurrentDashboardViewReason$.next('');
    }

    get canSaveCurrentDashboardView(): boolean {
        return this.cannotSaveCurrentDashboardViewReason$.value === '';
    }

    async restoreInitialContext() {
        if (!this.INITIAL_QUERY_CONTEXT) {
            throw new Error('Initial context not set');
        }
        this.queryContextService.initializeContext(this.INITIAL_QUERY_CONTEXT);
        this.initialQueryContextRestored$.next();

        // remove contextId from URL
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { contextId: null },
            queryParamsHandling: 'merge',
        });
    }

    /**
     * Share current DashboardView.
     * @param name - The friendly name of the saved context.
     * @param isPublic - Indicates whether the saved context is public or private.
     */
    async saveDashboardView(options?: {
        name?: string;
        isPublic?: boolean;
    }): Promise<string | undefined> {
        // context cannot be unnamed and public
        if (!options?.name && options?.isPublic) {
            throw new Error('Public context must have a name');
        }

        // get current query context
        const currentQueryContext =
            this.queryContextService.getAppliedQueryContext();

        const currentUserId = this.authService.auth.currentUser?.email;
        if (!currentUserId) {
            throw new Error('User is not logged in');
        }

        let currentContextWithMetadata: Omit<DashboardView, 'id'> = {
            userEmail: currentUserId,
            createdAt: new Date(),
            dashboardId: this.DASHBOARD_ID,
            appVersion: environment.version,
            context: currentQueryContext,
            dashboardContext: this._currentDashboardContext,
        };
        if (options?.name) {
            currentContextWithMetadata.name = options.name;
        }
        if (options?.isPublic) {
            currentContextWithMetadata.isPublic = options.isPublic;
        }

        try {
            const doc = await this.data.addSavedContext(
                currentContextWithMetadata
            );

            // if name is provided, emit event to update
            if (options?.name) {
                // emit event to update
                this.newDashboardViewSaved$.next(doc.id);
            }

            this.currentDashboardView =
                (await this.data.getSavedDashboardView(doc.id)) ?? null;

            const currentContextId = doc.id;
            await this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { contextId: currentContextId },
                queryParamsHandling: 'merge',
            });

            // copy url to clipboard
            const url = window.location.href;
            await navigator.clipboard.writeText(url);

            this.notification.success(
                'Context shared',
                'Context shared successfully. Url copied to clipboard.'
            );
            return doc.id;
        } catch (e: any) {
            this.notification.error('Error saving context', e.message);
            this.logger.error('Error saving context', e);
        }
        return undefined;
    }

    /**
     * Saves the current query context with a friendly name and visibility.
     * Can overwrite existing context if the context ID is provided.
     */
    async saveAsNamedDashboardView(contextMeta: SavedContextMetadata) {
        // if not override than add new context
        if (!contextMeta.overwrite) {
            await this.saveDashboardView({
                name: contextMeta.name,
                isPublic: contextMeta.isPublic,
            });
        }
        if (contextMeta.overwrite) {
            if (!contextMeta.id) {
                throw new Error('Context ID is required for overwrite.');
            }
            const dashboardView = await this.data.getSavedDashboardView(
                contextMeta.id
            );
            // should not happen
            if (!dashboardView) {
                this.notification.error(
                    'Dashboard view not found',
                    `Dashboard view with context ID '${contextMeta.id}' not found.`
                );
                throw new Error('Dashboard view not found');
            }
            dashboardView.name = contextMeta.name;
            dashboardView.isPublic = contextMeta.isPublic;

            await this.updateDashboardView(dashboardView);
            this.newDashboardViewSaved$.next(contextMeta.id);
            await this.loadSavedDashboardView(contextMeta.id);
        }
    }

    /**
     * Updates dashboard view with current query and dashboard context.
     */
    async updateDashboardView(dashboardView: DashboardView) {
        if (!dashboardView) {
            throw new Error('No DashboardView is selected');
        }
        let currentContext = this.queryContextService.getAppliedQueryContext();
        dashboardView.context = currentContext;
        dashboardView.dashboardContext = this._currentDashboardContext;

        await this.data.setSavedContext(dashboardView.id, dashboardView);

        this.notification.success(
            'Dashboard view updated',
            `Dashboard view '${dashboardView.name}' (${dashboardView.id}) updated successfully.`
        );
    }

    /**
     * Sets the current dashboard context.
     *
     * Stores the provided context and optionally notifies subscribers.
     *
     * @param dashboardContext - The new dashboard context.
     * @param emitEvent - If true, emits updated context to subscribers.
     */
    public setDashboardContext(
        dashboardContext: IDashboardContext,
        emitEvent = true
    ) {
        this._currentDashboardContext = dashboardContext
            ? { ...dashboardContext }
            : {};
        if (emitEvent) {
            this.currentDashboardContext$.next(this._currentDashboardContext);
        }
    }

    /**
     * Loads a saved dashboard view by context ID.
     * @param contextId - The context ID of the saved dashboard view
     * to load.
     * @returns Promise that resolves when the saved dashboard view is loaded.
     * @throws Error if the context ID is not provided.
     */
    async loadSavedDashboardView(
        contextId: string,
        options?: {
            /** If true, reroutes to the new context ID in the URL. Default is true. */
            reroute?: boolean;
        }
    ) {
        let dashboardView = await this.data.getSavedDashboardView(contextId);
        if (!dashboardView) {
            this.notification.error(
                'Dashboard view not found',
                `Dashboard view with context ID '${contextId}' not found.`
            );
            throw new Error('Dashboard view not found');
        }

        try {
            // query context
            this.queryContextService.initializeContext(dashboardView.context);
            // dashboard context
            this.setDashboardContext(
                dashboardView.dashboardContext ?? {},
                true
            );
            this.currentDashboardView = dashboardView;
            let notificationMessage = (() => {
                if (dashboardView['name']) {
                    return `Dashboard view '${dashboardView['name']} (${dashboardView['id']})' loaded successfully.`;
                } else {
                    return `Unnamed dashboard view '${dashboardView['id']}' loaded successfully.`;
                }
            })();
            // this.notification.success(
            //     'Dashboard view loaded',
            //     notificationMessage
            // );
        } catch (error) {
            this.logger.error(
                `[DashboardService.applySavedContext] Error applying context`,
                error
            );
        }
        // add contextId to url
        if (options?.reroute ?? true) {
            await this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { contextId: dashboardView.id },
                queryParamsHandling: 'merge',
            });
        }
    }

    /**
     * Gets list of named dashboard views for the dashboard.
     * @returns Promise that resolves with the named dashboard views.
     */
    public async getNamedDashboardViews(): Promise<DashboardView[]> {
        const dashboardViews = await this.data.getNamedContexts({
            dashboardId: this.DASHBOARD_ID,
        });

        const sortFn = this.dashboardInitOptions.savedDashboardViewsCompareFn
            ? this.dashboardInitOptions.savedDashboardViewsCompareFn
            : SavedDashboardViewHelper.compareDashboardViewsByName;
        this.savedDashboardViews = dashboardViews.sort(sortFn);

        return this.savedDashboardViews;
    }

    /**
     * Updates current dashboard context with provided dashboard items.
     * @param dashboardItems - The dashboard items to update the context with.
     * @param emitEvent - If true, emits updated context to subscribers.
     * @throws Error if the dashboard context is not initialized.
     */
    public setDashboardItemPersistentOptions(
        dashboardItems: IDashboardContext['dashboardItems'],
        emitEvent = true
    ) {
        if (!this._currentDashboardContext) {
            throw new Error('Dashboard context is not initialized');
        }

        if (!this._currentDashboardContext.dashboardItems) {
            this._currentDashboardContext.dashboardItems = {};
        }

        // merge new options with existing
        this._currentDashboardContext.dashboardItems = {
            ...this._currentDashboardContext.dashboardItems,
            ...dashboardItems,
        };

        if (emitEvent) {
            this.currentDashboardContext$.next(this._currentDashboardContext);
        }
    }

    /**
     * Orchestrates ETLs.
     * This function is used to trigger the orchestration of BI needed to reprocess newly created segment with all its dependencies.
     */
    public async requestNewSegmentsProcessing(modal: NzModalService) {
        const orchestrateBiFromSegments = async () => {
            this.logger.info('requestNewSegmentsProcessing started');
            await this.func.orchestrateBiFromSegments();
            this.logger.info('requestNewSegmentsProcessing finished');
        };

        modal.confirm({
            nzTitle: 'Did you add new segments?',
            nzContent: /* HTML */ `<p>
                    If you added new segments definitions to BQ, please click
                    "OK" to process them.
                </p>
                <p>Wait about 15 minutes and then refresh the dashboard.</p>
                <a target="_blank" href="${this.config.newSegmentWikiLink}"
                    >Learn more</a
                >`,
            nzOnOk: () => {
                return orchestrateBiFromSegments();
            },
        });
    }

    /**
     * Shows the chart documentation modal.
     * This function is static and can be called without an instance of the class.
     * @param modal - The modal service to use for displaying the modal.
     * @param content - The content to display in the modal.
     */
    static showChartDocumentation(
        modal: NzModalService,
        title: string,
        content: any
    ) {
        modal.create({
            nzTitle: `${title} documentation`,
            nzContent: content,
            nzClosable: true,
            nzFooter: null,
            nzWidth: '70%',
        });
    }

    /**
     * Shows the dashboard documentation modal.
     * @param modal - The modal service to use for displaying the modal.
     * @param content - The content to display in the modal.
     */
    public showDashboardDocumentation(content: any) {
        this.modal.create({
            nzTitle: 'Dashboard Documentation',
            nzContent: content,
            nzClosable: true,
            nzFooter: null,
            nzWidth: '70%',
        });
    }
}
